import React from "react";
import { CookiesProvider } from "react-cookie";
import { BrowserRouter } from "react-router-dom";
import Router from "./Router";
import { Global, css } from "@emotion/react";
import * as Sentry from "@sentry/react";

import {
  AllocationsStore,
  AllocationGroupsStore,
  ApiStore,
  AuthStore,
  CsesStore,
  CorpUsersStore,
  DelayedDeliveriesStore,
  DeliveriesPickupsStore,
  DeliveriesReturnsStore,
  DeliveriesStore,
  DeliveriesMonthlyStore,
  DeliveriesKurlyStore,
  DeliveriesCjthemarketStore,
  DeliveriesMisclassificationStore,
  DeliveriesOliveyoungQuickStore,
  ErrorStore,
  HanjinReturnWaybillsStore,
  LoadingStore,
  LostDeliveriesDisposeApprovalsStore,
  ModalStore,
  RiderGroupsStore,
  RidersStore,
  UsersStore,
} from "./store";

import "bootstrap/dist/css/bootstrap.min.css";

const GlobalStyle = css`
  body {
    font-size: 14px;
  }

  *,
  :after,
  :before {
    box-sizing: border-box;
  }

  .col-form-label {
    font-size: 16px;
  }
`;

const SentryErrorBoundary = Sentry.ErrorBoundary;

const AppProvider = ({ contexts, children }) =>
  contexts.reduce(
    (prev, context) =>
      React.createElement(context, {
        children: prev,
      }),
    children,
  );

function App() {
  // 500번대 에러 상태 코드와 설명 매핑
  const SERVER_ERROR_TYPES = {
    500: {
      name: "internal_server_error",
      description: "Internal Server Error",
    },
    501: { name: "not_implemented", description: "Not Implemented" },
    502: { name: "bad_gateway", description: "Bad Gateway" },
    503: { name: "service_unavailable", description: "Service Unavailable" },
    504: { name: "gateway_timeout", description: "Gateway Timeout" },
    505: {
      name: "http_version_not_supported",
      description: "HTTP Version Not Supported",
    },
    506: {
      name: "variant_also_negotiates",
      description: "Variant Also Negotiates",
    },
    507: { name: "insufficient_storage", description: "Insufficient Storage" },
    508: { name: "loop_detected", description: "Loop Detected" },
    509: {
      name: "bandwidth_limit_exceeded",
      description: "Bandwidth Limit Exceeded",
    },
    510: { name: "not_extended", description: "Not Extended" },
    511: {
      name: "network_authentication_required",
      description: "Network Authentication Required",
    },
  };

  // 500번대 에러 체크 유틸리티 함수
  const is5xxError = (error) => {
    const status =
      error?.status || error?.statusCode || error?.response?.status;
    if (status && status >= 500 && status <= 599) {
      return status;
    }

    // 에러 메시지에서 상태 코드 찾기
    if (error?.message) {
      const match = error.message.match(/\b5\d{2}\b/);
      if (match) {
        return parseInt(match[0]);
      }
    }

    return null;
  };

  return (
    <>
      <Global styles={GlobalStyle} />
      <SentryErrorBoundary
        fallback={({ error, resetError }) => {
          const statusCode = is5xxError(error);
          return (
            <div style={{ padding: "20px", textAlign: "center" }}>
              <h1>오류가 발생했습니다</h1>
              <p>오류: {error.message}</p>
              {statusCode && <p>상태 코드: {statusCode}</p>}
              <button onClick={resetError}>다시 시도</button>
            </div>
          );
        }}
        showDialog={process.env.REACT_APP_ENV === "production"}
        beforeCapture={(scope, error) => {
          const statusCode = is5xxError(error);
          if (statusCode) {
            const errorType = SERVER_ERROR_TYPES[statusCode] || {
              name: `server_error_${statusCode}`,
            };

            scope.setTag("error_category", "5xx_error");
            scope.setTag("error_type", errorType.name);
            scope.setTag("http_status", statusCode.toString());
          }
        }}
      >
        <CookiesProvider>
          <AppProvider
            contexts={[
              AllocationsStore.Provider,
              AllocationGroupsStore.Provider,
              ApiStore.Provider,
              AuthStore.Provider,
              CsesStore.Provider,
              CorpUsersStore.Provider,
              DelayedDeliveriesStore.Provider,
              DeliveriesPickupsStore.Provider,
              DeliveriesReturnsStore.Provider,
              DeliveriesStore.Provider,
              DeliveriesMonthlyStore.Provider,
              DeliveriesKurlyStore.Provider,
              DeliveriesCjthemarketStore.Provider,
              DeliveriesMisclassificationStore.Provider,
              DeliveriesOliveyoungQuickStore.Provider,
              ErrorStore.Provider,
              HanjinReturnWaybillsStore.Provider,
              LoadingStore.Provider,
              LostDeliveriesDisposeApprovalsStore.Provider,
              ModalStore.Provider,
              RiderGroupsStore.Provider,
              RidersStore.Provider,
              UsersStore.Provider,
            ]}
          >
            <BrowserRouter>
              <Router />
            </BrowserRouter>
          </AppProvider>
        </CookiesProvider>
      </SentryErrorBoundary>
    </>
  );
}

export default App;
